import React, { useState, useEffect } from "react";
import { getCurrentUserId, getMyStudentId } from "../../services/api";
import Layout from "../../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Agents.css";
import "./ProgressMonitor.css";
import { getApiBaseUrl } from "../../services/apiBase.js";

const API_BASE_URL = getApiBaseUrl();

/**
 * INTUITIVE PROGRESS MONITOR
 * Clean, visual, and insight-focused
 */

const getAuthToken = () => {
  return (
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token")
  );
};

const getStudentId = () => {
  return getMyStudentId();
};

// ---- Display helpers: never render "undefined", "NaN" or empty "%" ----
const isNum = (v) =>
  v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));
const fmtNum = (v, fallback = "\u2014") =>
  isNum(v) ? Math.round(Number(v) * 10) / 10 : fallback;
const fmtPct = (v, fallback = "\u2014") =>
  isNum(v) ? `${Math.round(Number(v) * 10) / 10}%` : fallback;
// plural(1, "day") -> "1 day", plural(3, "day") -> "3 days"
const plural = (n, singular, pluralForm = `${singular}s`) => {
  const value = isNum(n) ? Number(n) : 0;
  return `${value} ${value === 1 ? singular : pluralForm}`;
};
// "fractions" -> "Fractions", "basic_science" -> "Basic Science"
const toTitleCase = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) =>
      w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w)
        ? w
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(" ");
};
const formatLabel = (code) => {
  if (!code) return "";
  const words = String(code).replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
};
// Topic score entries may be plain numbers or objects from the API
const scoreOf = (value) => {
  if (value && typeof value === "object") {
    return Number(value.average_score ?? value.score ?? value.percentage);
  }
  return Number(value);
};
const textOf = (value) => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    return value.text || value.message || value.recommendation || "";
  }
  return "";
};

const ProgressMonitor = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [timeframe, setTimeframe] = useState("all");
  const [selectedView, setSelectedView] = useState("overview");

  const navigate = useNavigate();

  useEffect(() => {
    loadProgress();
  }, [timeframe]);

  // Listen for quiz completion events
  useEffect(() => {
    const handleQuizCompleted = (event) => {
      console.log(
        "📊 Quiz completed event received in Progress Monitor:",
        event.detail
      );
      setTimeout(() => {
        loadProgress();
      }, 2000); // Small delay to ensure backend processing is complete
    };

    window.addEventListener("quizCompleted", handleQuizCompleted);
    return () =>
      window.removeEventListener("quizCompleted", handleQuizCompleted);
  }, []);

  const loadProgress = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("Please login to view your progress");
      }

      const studentId = getStudentId();
      const response = await axios.post(
        `${API_BASE_URL}/api/agents/progress/monitor`,
        {
          student_id: studentId,
          action: "check_progress",
          timeframe: timeframe,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("📊 Progress Monitor - Full Response:", response.data);
      console.log(
        "📊 Progress Monitor - Response Keys:",
        Object.keys(response.data)
      );
      console.log("📊 Progress Monitor - Success:", response.data.success);
      console.log("📊 Progress Monitor - Metrics:", response.data.metrics);

      if (response.data && response.data.success) {
        console.log("✅ Setting progress data:", response.data);
        setProgressData(response.data);
        setLoading(false);
      } else {
        throw new Error(response.data?.error || "Failed to fetch progress");
      }
    } catch (err) {
      console.error("Progress fetch error:", err);
      setError(
        err.response?.data?.message || err.message || "Failed to load progress"
      );
      setLoading(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="progress-intuitive-container loading-state">
        <div className="loading-box">
          <div className="spinner-large"></div>
          <h2>Loading your progress...</h2>
          <p>Analyzing your learning data</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="progress-intuitive-container error-state">
        <div className="error-box">
          <div className="error-icon">⚠️</div>
          <h2>Oops! Something went wrong</h2>
          <p className="error-message">{error}</p>
          <button className="btn-primary-large" onClick={loadProgress}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty State
  console.log("📊 Checking empty state:", {
    hasProgressData: !!progressData,
    hasMetrics: !!progressData?.metrics,
    totalAttempts: progressData?.metrics?.total_attempts,
    topicScores: progressData?.topic_scores,
  });

  if (
    !progressData ||
    !progressData.metrics ||
    (progressData.metrics.total_attempts === 0 &&
      (!progressData.topic_scores ||
        Object.keys(progressData.topic_scores).length === 0))
  ) {
    return (
      <div className="progress-intuitive-container empty-state">
        <div className="empty-box">
          <div className="empty-icon">📚</div>
          <h2>Start Your Learning Journey</h2>
          <p>
            Take quizzes to see your progress and get personalized insights!
          </p>
          <button
            className="btn-primary-large"
            onClick={() => navigate("/student-dashboard")}
          >
            Go to Dashboard →
          </button>
        </div>
      </div>
    );
  }

  const { metrics, topic_scores } = progressData;
  const insights = progressData.insights || {};
  const trends = progressData.trends || {};
  const patterns = progressData.patterns;
  const mastery = metrics.mastery || {};
  const streaks = metrics.streaks || {};
  const totalAttempts = isNum(metrics.total_attempts)
    ? Number(metrics.total_attempts)
    : 0;

  // Hero Progress Section
  const HeroProgress = () => {
    const getMasteryColor = (level) => {
      const colors = {
        Beginner: "#3b82f6",
        Developing: "#8b5cf6",
        Intermediate: "#f59e0b",
        Proficient: "#10b981",
        Advanced: "#06b6d4",
        Expert: "#f43f5e",
      };
      return colors[level] || "#64748b";
    };

    const getMasteryIcon = (level) => {
      const icons = {
        Beginner: "🌱",
        Developing: "🌿",
        Intermediate: "🌳",
        Proficient: "🎯",
        Advanced: "🏆",
        Expert: "👑",
      };
      return icons[level] || "📖";
    };

    const getStreakEmoji = (days) => {
      if (days >= 30) return "🏆";
      if (days >= 14) return "🔥";
      if (days >= 7) return "⚡";
      if (days >= 3) return "✨";
      return "🌟";
    };

    return (
      <div className="hero-progress">
        <div className="hero-mastery">
          <div
            className="mastery-circle"
            style={{ borderColor: getMasteryColor(mastery.level) }}
          >
            <div className="mastery-icon-large">
              {getMasteryIcon(mastery.level)}
            </div>
            <h2>{formatLabel(mastery.level) || "Getting started"}</h2>
            {isNum(mastery.percentage) && <p>{fmtPct(mastery.percentage)}</p>}
          </div>
          <div className="mastery-info">
            <h3>Your Mastery Level</h3>
            {mastery.next_level && (
              <p>
                Next level: <strong>{formatLabel(mastery.next_level)}</strong>
              </p>
            )}
            <p className="mastery-desc">Keep learning to advance!</p>
          </div>
        </div>

        <div className="hero-streak">
          <div className="streak-icon-large">
            {getStreakEmoji(Number(streaks.current) || 0)}
          </div>
          <div className="streak-info">
            <h2>{fmtNum(streaks.current, 0)}</h2>
            <p>Day streak</p>
            {isNum(streaks.longest) && (
              <span className="streak-best">
                Best: {plural(streaks.longest, "day")}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Quick Stats
  const QuickStats = () => (
    <div className="quick-stats-progress">
      {[
        {
          label: totalAttempts === 1 ? "Quiz Taken" : "Quizzes Taken",
          value: totalAttempts,
          icon: "📝",
        },
        {
          label: "Average Score",
          value: fmtPct(metrics.average_score),
          icon: "🎯",
        },
        {
          label: "Highest Score",
          value: fmtPct(metrics.highest_score),
          icon: "⭐",
        },
        {
          label:
            Number(metrics.topics?.total) === 1
              ? "Topic Studied"
              : "Topics Studied",
          value: fmtNum(metrics.topics?.total, 0),
          icon: "📚",
        },
      ].map((stat, idx) => (
        <div key={idx} className="stat-box">
          <div className="stat-icon">{stat.icon}</div>
          <div className="stat-value">{stat.value}</div>
          <div className="stat-label">{stat.label}</div>
        </div>
      ))}
    </div>
  );

  // Timeframe Selector
  const TimeframeSelector = () => (
    <div className="timeframe-tabs">
      {[
        { value: "week", label: "Week", icon: "📅" },
        { value: "month", label: "Month", icon: "📆" },
        { value: "quarter", label: "Quarter", icon: "📊" },
        { value: "all", label: "All Time", icon: "⏳" },
      ].map((option) => (
        <button
          key={option.value}
          onClick={() => setTimeframe(option.value)}
          className={`timeframe-tab ${
            timeframe === option.value ? "active" : ""
          }`}
        >
          <span>{option.icon}</span>
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );

  // View Tabs
  const ViewTabs = () => (
    <div className="view-tabs">
      {[
        { id: "overview", label: "Overview", icon: "📊" },
        { id: "topics", label: "Topics", icon: "📚" },
        { id: "insights", label: "Insights", icon: "💡" },
      ].map((tab) => (
        <button
          key={tab.id}
          onClick={() => setSelectedView(tab.id)}
          className={`view-tab ${selectedView === tab.id ? "active" : ""}`}
        >
          <span>{tab.icon}</span>
          <span>{tab.label}</span>
        </button>
      ))}
    </div>
  );

  // Trend Indicator
  const TrendIndicator = () => {
    const getTrendIcon = (direction) => {
      if (direction === "up") return "📈";
      if (direction === "down") return "📉";
      return "➡️";
    };

    const getTrendColor = (direction) => {
      if (direction === "up") return "#10b981";
      if (direction === "down") return "#ef4444";
      return "#64748b";
    };

    const hasComparison =
      isNum(trends.recent_average) && isNum(trends.older_average);

    const getTrendMessage = () => {
      if (!hasComparison) return "Your trend is on its way";
      if (trends.trend === "improving") return "You're improving! 🎉";
      if (trends.trend === "declining") return "Needs attention 💪";
      return "Staying consistent 👍";
    };

    return (
      <div
        className="trend-box"
        style={{ borderColor: getTrendColor(trends.direction) }}
      >
        <div className="trend-icon-large">{getTrendIcon(trends.direction)}</div>
        <div className="trend-content">
          <h3>{getTrendMessage()}</h3>
          {hasComparison ? (
            <>
              {isNum(trends.change) && Number(trends.change) !== 0 && (
                <p
                  className="trend-change"
                  style={{ color: getTrendColor(trends.direction) }}
                >
                  {Number(trends.change) > 0 ? "+" : ""}
                  {fmtPct(trends.change)} change
                </p>
              )}
              <div className="trend-comparison">
                <span>Recent: {fmtPct(trends.recent_average)}</span>
                <span>Previous: {fmtPct(trends.older_average)}</span>
              </div>
            </>
          ) : (
            <p className="trend-change trend-change-empty">
              Take another quiz to see your trend
            </p>
          )}
        </div>
      </div>
    );
  };

  // Performance Distribution
  const PerformanceDistribution = () => {
    const rawDist = metrics.performance_distribution || {};
    const dist = {
      excellent: Number(rawDist.excellent) || 0,
      good: Number(rawDist.good) || 0,
      passing: Number(rawDist.passing) || 0,
      needs_work: Number(rawDist.needs_work ?? rawDist.failing) || 0,
    };
    const total = dist.excellent + dist.good + dist.passing + dist.needs_work;

    if (total === 0) return null;

    const segments = [
      {
        label: "Excellent",
        count: dist.excellent,
        color: "#10b981",
        icon: "🌟",
      },
      {
        label: "Good",
        count: dist.good,
        color: "#3b82f6",
        icon: "👍",
      },
      {
        label: "Passing",
        count: dist.passing,
        color: "#f59e0b",
        icon: "✓",
      },
      {
        label: "Needs Work",
        count: dist.needs_work,
        color: "#ef4444",
        icon: "📌",
      },
    ];

    return (
      <div className="performance-dist">
        <h3>📊 Performance Distribution</h3>
        <div className="dist-bars">
          {segments.map((segment) => {
            const percentage = total > 0 ? (segment.count / total) * 100 : 0;
            return (
              <div key={segment.label} className="dist-segment">
                <div className="segment-header">
                  <span className="segment-icon">{segment.icon}</span>
                  <span className="segment-label">{segment.label}</span>
                  <span className="segment-count">{segment.count}</span>
                </div>
                <div className="segment-bar-bg">
                  <div
                    className="segment-bar-fill"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: segment.color,
                    }}
                  />
                </div>
                <span className="segment-percent">
                  {percentage.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Topic Performance Cards
  const TopicPerformance = () => {
    const getScoreColor = (score) => {
      if (score >= 85) return "#10b981";
      if (score >= 70) return "#3b82f6";
      if (score >= 60) return "#f59e0b";
      return "#ef4444";
    };

    const sortedTopics = Object.entries(topic_scores || {})
      .map(([topic, value]) => [topic, scoreOf(value)])
      .filter(([, score]) => Number.isFinite(score))
      .sort((a, b) => b[1] - a[1]);

    if (sortedTopics.length === 0) {
      return (
        <div className="empty-topics">
          <p>No topic data yet. Start taking quizzes!</p>
        </div>
      );
    }

    return (
      <div className="topics-grid-simple">
        {sortedTopics.map(([topic, score]) => {
          const isStrength = insights.strengths?.some((s) => s.topic === topic);
          const isWeakness = insights.weaknesses?.some(
            (w) => w.topic === topic
          );
          const isImproving = insights.improving?.some(
            (i) => i.topic === topic
          );

          return (
            <div key={topic} className="topic-card-simple">
              <div className="topic-header-simple">
                <h4>{toTitleCase(topic)}</h4>
                {isStrength && <span className="badge-strength">⭐</span>}
                {isWeakness && <span className="badge-weakness">📌</span>}
                {isImproving && <span className="badge-improving">📈</span>}
              </div>

              <div className="topic-score-display">
                <div
                  className="score-circle-large"
                  style={{ borderColor: getScoreColor(score) }}
                >
                  <span style={{ color: getScoreColor(score) }}>
                    {fmtPct(score)}
                  </span>
                </div>
              </div>

              <div className="topic-bar-wrapper">
                <div className="topic-bar-bg">
                  <div
                    className="topic-bar-fill"
                    style={{
                      width: `${Math.min(100, Math.max(0, score))}%`,
                      backgroundColor: getScoreColor(score),
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Insights Cards
  const InsightsCards = () => {
    const recommendations = (
      Array.isArray(insights.recommendations) ? insights.recommendations : []
    )
      .map(textOf)
      .filter(Boolean);
    const improving = Array.isArray(insights.improving) ? insights.improving : [];
    const declining = Array.isArray(insights.declining) ? insights.declining : [];

    if (!recommendations.length && !improving.length && !declining.length) {
      return (
        <div className="empty-topics">
          <p>
            No insights yet. Take a few more quizzes and we'll highlight your
            progress here.
          </p>
        </div>
      );
    }

    return (
    <div className="insights-grid">
      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="insight-card">
          <h3>💡 Recommendations</h3>
          <ul className="insight-list">
            {recommendations.map((rec, idx) => (
              <li key={idx}>{rec}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Improving Topics */}
      {improving.length > 0 && (
        <div className="insight-card success-card">
          <h3>📈 Great Progress!</h3>
          <ul className="insight-list">
            {improving.map((item, idx) => (
              <li key={idx}>
                <span className="topic-name">{toTitleCase(item.topic)}</span>
                {isNum(item.improvement) && (
                  <span className="badge-success">
                    +{fmtPct(item.improvement)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Declining Topics */}
      {declining.length > 0 && (
        <div className="insight-card warning-card">
          <h3>⚠️ Needs Attention</h3>
          <ul className="insight-list">
            {declining.map((item, idx) => (
              <li key={idx}>
                <span className="topic-name">{toTitleCase(item.topic)}</span>
                {isNum(item.decline) && (
                  <span className="badge-warning">
                    -{fmtPct(Math.abs(Number(item.decline)))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
    );
  };

  // Learning Patterns
  const LearningPatterns = () => {
    if (!patterns || Object.keys(patterns).length === 0) return null;

    const getTimeDisplay = (rawHour) => {
      if (!isNum(rawHour)) return "Not enough data";
      const hour = Number(rawHour);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:00 ${period}`;
    };

    return (
      <div className="patterns-box">
        <h3>🎯 Your Learning Patterns</h3>
        <div className="patterns-grid-simple">
          <div className="pattern-item">
            <div className="pattern-icon">⏰</div>
            <p className="pattern-label">Most Active Time</p>
            <p className="pattern-value">
              {getTimeDisplay(patterns.most_active_hour)}
            </p>
          </div>

          <div className="pattern-item">
            <div className="pattern-icon">📅</div>
            <p className="pattern-label">Favorite Day</p>
            <p className="pattern-value">
              {patterns.most_active_day || "Not enough data"}
            </p>
          </div>

          <div className="pattern-item">
            <div className="pattern-icon">🔄</div>
            <p className="pattern-label">Study Frequency</p>
            <p className="pattern-value">
              {isNum(patterns.study_frequency)
                ? fmtPct(patterns.study_frequency)
                : formatLabel(patterns.study_frequency) || "Not enough data"}
            </p>
          </div>

          <div className="pattern-item">
            <div className="pattern-icon">📊</div>
            <p className="pattern-label">Total Sessions</p>
            <p className="pattern-value">
              {fmtNum(patterns.total_sessions, 0)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Main Render
  return (
    <div className="progress-intuitive-container">
      <div className="progress-intuitive-content">
        {/* Header */}
        <div className="progress-header-intuitive">
          <div>
            <h1>📊 Progress Monitor</h1>
            <p>Track your learning journey and insights</p>
          </div>
          <button
            className="btn-back-simple"
            onClick={() => navigate("/student-dashboard")}
          >
            ← Back
          </button>
        </div>

        {/* Timeframe Selector */}
        <TimeframeSelector />

        {/* Hero Section */}
        <HeroProgress />

        {/* Quick Stats */}
        <QuickStats />

        {/* View Tabs */}
        <ViewTabs />

        {/* Overview View */}
        {selectedView === "overview" && (
          <>
            <TrendIndicator />
            <PerformanceDistribution />
            <LearningPatterns />
          </>
        )}

        {/* Topics View */}
        {selectedView === "topics" && (
          <>
            <h2 className="section-title-simple">📚 Topic Performance</h2>
            <TopicPerformance />
          </>
        )}

        {/* Insights View */}
        {selectedView === "insights" && (
          <>
            <h2 className="section-title-simple">💡 Personalized Insights</h2>
            <InsightsCards />
          </>
        )}

        {/* Footer Tip */}
        <div className="footer-tip-simple">
          💡 <strong>Pro Tip:</strong> Consistent daily practice is key to
          improvement. Try to maintain your streak and focus on weak areas for
          balanced growth!
        </div>
      </div>
    </div>
  );
};

// Wrap in the shared layout so the page has the site header and footer
const ProgressMonitorPage = (props) => (
  <Layout>
    <ProgressMonitor {...props} />
  </Layout>
);

export default ProgressMonitorPage;
